import { useEffect, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { ApiError, api } from '../api/client.js';
import { queries } from '../store/queries.js';
import { useQuery } from '../store/StoreProvider.js';
import { setAlertsSwitchedOff } from './alertsSignal.js';
import type { Me } from '../api/types.js';
import { color, elevation, fontSize, layout, radius } from '../theme/tokens.js';

/**
 * The application shell's sidebar (FR-018, FR-020, FR-023, Constitution Principle I).
 *
 * All six blueprint WORKSPACE entries render, because the shell must match the blueprint. **Three**
 * of them — Audit, Alerts, Leaderboard — are out of scope for this release and are
 * made genuinely non-interactive (FR-023):
 *
 * - visibly dimmed;
 * - `tabIndex={-1}` plus `pointer-events: none`, so neither pointer nor keyboard reaches them;
 * - rendered as `<span>`, not `<a>`, so there is no href to follow and no route to type.
 *
 * A dimmed link that still navigates would be worse than omitting it: it looks disabled and
 * behaves enabled.
 *
 * **The collapsed rail keeps the page name under the icon** (owner decision, 2026-08-28). It
 * replaces the blueprint's 7px accent dot, which was the entry's only mark once collapsed — six
 * coloured squares are not a legend, so the rail could only be read by hovering each one in turn.
 * Consequences worth knowing before editing: the rail is `layout.sidebarCollapsed` wide rather
 * than icon-width, the active wash moves onto the **icon tile** (a full-width pill behind a
 * centred column reads as a selected block, not a selected item), and the count badge is
 * expanded-only — a number beside a wrapped label is indistinguishable from part of it.
 *
 * **Audit became live with spec 007**, and it is dimmed only once the server has SAID it is off.
 * Its state comes from the prefetched Audit list (`queries.auditList()`): a 503 `AUDIT_DISABLED`
 * leaves it exactly as dimmed and unreachable as Alerts and Leaderboard (FR-AU38); anything else
 * — a response, a request still in flight, an upstream failure — leaves it live, and a response
 * adds the badge, the number of audits listed (`auditCount`, catalogued or not). There is no build
 * flag — an operator switches the page without a release.
 * ⚠ **"No response yet" is live, not dimmed** (owner report 2026-10-14). It used to dim, and on
 * sign-in the uncached List takes ~14 s of run discovery, so for that whole time the entry looked
 * switched off — a loading page presented as an unavailable one. The page now shows its skeleton
 * while the List is in flight. The cost runs the other way and is small: with the switch OFF, the
 * entry is live for the one fast round trip it takes the 503 to come back.
 * ⚠ **An upstream failure is live too**: it is the audit service being unreachable, not the page
 * being switched off, and the page is where FR-AU37's error and Retry are — a dimmed entry would
 * hide the one control that could recover it.
 *
 * **Delivery became live with spec 002** and carries the blueprint's monospace count badge showing
 * the number of leaders (PRD-Delivery §6.1). The badge is a small fetch of its own rather than a
 * prop threaded from whichever page happens to be open, so the shell does not depend on the route.
 *
 * Settings appears only for `admin` (FR-020) — **presentation only**. The server enforces the
 * actual control on every `/api/settings/*` request (FR-046), and hiding the entry is never
 * the authorization.
 *
 * **The rail is the resting state, and a hover PEEKS it open** (owner request 2026-09-10). Two
 * pieces of state, not one: `pinned` is the reader's choice (the button, which is now a pin and
 * no longer a collapse toggle) and `peeking` is the pointer or the keyboard being inside. What
 * makes the peek safe is that it is an **overlay** — the `<aside>` keeps the *rail's* width in
 * the flex flow whenever it is unpinned, and the drawer is an absolutely positioned panel that
 * hangs over the page. Expanding it in flow instead would reflow `<main>` on every passing
 * hover: this product is a wall of figures read column by column, and every one of them would
 * slide 140px sideways while the pointer travelled to the one being read. That also fixes the
 * drawer's `zIndex` above the page header's, since the header sits later in the DOM.
 */

/**
 * A 24-grid line icon on `currentColor`, so one glyph serves the dimmed, resting, hover and
 * active inks without a second copy per state.
 */
function NavIcon({ children }: { children: ReactNode }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flex: 'none' }}
    >
      {children}
    </svg>
  );
}

interface NavEntry {
  label: string;
  to?: string;
  icon: ReactNode;
  enabled: boolean;
  /** A monospace count beside the label — the blueprint's shell treatment. Expanded only. */
  badge?: number | null;
  /**
   * `alert` draws the badge in the blueprint's red pill (lines 68–71) — the Alerts entry's count of
   * recruiters on alert. The colour is AL-35's darker red, white text at 5.4:1.
   */
  badgeTone?: 'muted' | 'alert';
}

const WORKSPACE: NavEntry[] = [
  {
    label: 'Regions',
    to: '/regions',
    enabled: true,
    icon: (
      <NavIcon>
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
        <circle cx="12" cy="10" r="3" />
      </NavIcon>
    ),
  },
  {
    label: 'Delivery',
    to: '/delivery',
    enabled: true,
    icon: (
      <NavIcon>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </NavIcon>
    ),
  },
  {
    label: 'Specialties',
    to: '/specialties',
    enabled: true,
    icon: (
      <NavIcon>
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </NavIcon>
    ),
  },
  {
    label: 'Audit',
    enabled: false,
    icon: (
      <NavIcon>
        <path d="M9 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3" />
        <rect x="9" y="2" width="6" height="4" rx="1" />
        <path d="m9 14 2 2 4-4" />
      </NavIcon>
    ),
  },
  {
    label: 'Alerts',
    enabled: false,
    icon: (
      <NavIcon>
        <path d="M10.3 21a2 2 0 0 0 3.4 0" />
        <path d="M3.3 15.3A1 1 0 0 0 4 17h16a1 1 0 0 0 .7-1.7C19.4 14 18 12.5 18 8a6 6 0 0 0-12 0c0 4.5-1.4 6-2.7 7.3" />
      </NavIcon>
    ),
  },
  {
    label: 'Leaderboard',
    enabled: false,
    icon: (
      <NavIcon>
        <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
        <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
        <path d="M4 22h16" />
        <path d="M10 14.7V17c0 .6-.5 1-1 1.2-1.2.6-2 2-2 3.8" />
        <path d="M14 14.7V17c0 .6.5 1 1 1.2 1.2.6 2 2 2 3.8" />
        <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
      </NavIcon>
    ),
  },
];

const SETTINGS_ENTRY: NavEntry = {
  label: 'Settings',
  to: '/settings',
  enabled: true,
  icon: (
    <NavIcon>
      <path d="M20 7h-9" />
      <path d="M14 17H5" />
      <circle cx="17" cy="17" r="3" />
      <circle cx="7" cy="7" r="3" />
    </NavIcon>
  ),
};

export interface SidebarProps {
  me: Me | null;
  onSignOut?: () => void;
}

export function Sidebar({ me, onSignOut }: SidebarProps) {
  const [pinned, setPinned] = useState(false);
  const [peeking, setPeeking] = useState(false);
  const [leaderCount, setLeaderCount] = useState<number | null>(null);

  const expanded = pinned || peeking;

  // The Delivery badge. Fetched once, and a failure leaves the badge absent rather than showing
  // `0` — a zero would claim the business has no delivery leaders (FR-055).
  useEffect(() => {
    let cancelled = false;
    api
      .delivery({ period: '90d', source: 'all', targeting: 'all', specialty: 'all' })
      .then((envelope) => {
        if (!cancelled) setLeaderCount(envelope.data?.leaders.length ?? null);
      })
      .catch(() => {
        if (!cancelled) setLeaderCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const auditList = useQuery(queries.auditList());
  // A held copy keeps it live whatever the latest refetch said, as it always has.
  const auditSwitchedOff =
    auditList.data?.data == null &&
    auditList.error instanceof ApiError &&
    auditList.error.code === 'AUDIT_DISABLED';

  /*
   * Alerts (spec 008). The sidebar is mounted once per session, so it owns the ONE live Alerts query:
   * it keeps the prefetched copy fresh (12 h, and the 07:30 New York refetch) and the header bell
   * reads that copy passively. Live exactly like Audit: dimmed only once the server SAID it is off.
   */
  const alerts = useQuery(queries.auditAlerts());
  const alertsSwitchedOff =
    alerts.data?.data == null && alerts.error instanceof ApiError && alerts.error.code === 'AUDIT_DISABLED';
  useEffect(() => setAlertsSwitchedOff(alertsSwitchedOff), [alertsSwitchedOff]);

  const entries: NavEntry[] = [
    ...WORKSPACE.map((entry) => {
      if (entry.label === 'Delivery') return { ...entry, badge: leaderCount };
      if (entry.label === 'Audit' && !auditSwitchedOff) {
        return { ...entry, enabled: true, to: '/audit', badge: auditList.data?.data?.auditCount ?? null };
      }
      if (entry.label === 'Alerts' && !alertsSwitchedOff) {
        // Recruiters, never flags (FR-AL03) — and absent until the data lands, never `0`.
        return { ...entry, enabled: true, to: '/alerts', badge: alerts.data?.data?.total ?? null, badgeTone: 'alert' as const };
      }
      return entry;
    }),
    ...(me?.role === 'admin' ? [SETTINGS_ENTRY] : []),
  ];

  const initials =
    (me?.displayName ?? me?.email ?? '')
      .split(/[\s.@]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || '··';

  return (
    <aside
      // Pointer and focus both peek. Focus has to, or the rail's labels — which the collapsed
      // state does keep — are the only readable thing a keyboard reader ever gets, and the
      // sign-out button and the Delivery count are unreachable.
      //
      // `touch` is skipped deliberately: a tap fires the enter and the leave together, on press
      // and release, so the drawer would flash open over the page and fold again under the
      // finger. The pin button is the control on a device with no hover.
      onPointerEnter={(event) => {
        if (event.pointerType !== 'touch') setPeeking(true);
      }}
      onPointerLeave={() => setPeeking(false)}
      onFocus={() => setPeeking(true)}
      // React's `onBlur` is `focusout`, which BUBBLES — so tabbing from one nav link to the next
      // would fold the drawer and reopen it under the reader. The containment test keeps it open
      // while focus is still somewhere inside.
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPeeking(false);
      }}
      style={{
        // The FLOW width: the rail unless the drawer is pinned. A peek must not move this — see
        // the overlay note in the block comment above.
        width: pinned ? layout.sidebarExpanded : layout.sidebarCollapsed,
        flex: 'none',
        position: 'sticky',
        top: 0,
        height: '100vh',
        transition: 'width .16s ease',
        // Above the page header's own `zIndex: 20`. The header is inside `<main>`, later in the
        // DOM, so at equal z-index it would paint over the peeked drawer's right-hand 140px.
        zIndex: 30,
      }}
    >
      <div
        style={{
          // Absolute inside the sticky rail, so the drawer's width is free to exceed the space
          // the rail reserves.
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: expanded ? layout.sidebarExpanded : layout.sidebarCollapsed,
          background: color.sidebarBg,
          color: color.sidebarInk,
          display: 'flex',
          flexDirection: 'column',
          transition: 'width .16s ease',
          overflow: 'hidden',
          // Only while it actually hangs over the page. A pinned drawer is part of the layout,
          // and a shadow there draws a seam down the middle of the shell.
          boxShadow: expanded && !pinned ? elevation.palette : undefined,
        }}
      >
      <div
        style={{
          padding: expanded ? '22px 16px 18px' : '22px 10px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: expanded ? 11 : 8,
          borderBottom: `1px solid ${color.sidebarDivider}`,
          justifyContent: expanded ? 'flex-start' : 'center',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 34,
            height: 34,
            flex: 'none',
            borderRadius: radius['2xl'],
            background: `linear-gradient(150deg, ${color.brandFrom}, ${color.brandTo})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            color: color.onBrand,
            fontSize: 16,
          }}
        >
          B
        </div>

        {expanded ? (
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: fontSize.brand,
                color: color.onBrand,
                letterSpacing: '-.2px',
                whiteSpace: 'nowrap',
              }}
            >
              BoB Manager
            </div>
            <div
              style={{
                fontSize: fontSize.label,
                color: color.sidebarInkMuted,
                letterSpacing: '.3px',
                whiteSpace: 'nowrap',
              }}
            >
              HEALTHCARE DIVISION
            </div>
          </div>
        ) : null}

        <button
          type="button"
          // A pin, not a collapse: the rail is now the resting state and the hover does the
          // opening, so what a reader needs from a button is to stop it folding away again.
          onClick={() => setPinned((v) => !v)}
          aria-label={pinned ? 'Unpin navigation' : 'Keep navigation open'}
          aria-pressed={pinned}
          style={{
            width: 22,
            height: 22,
            flex: 'none',
            borderRadius: radius.md,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            background: color.sidebarHover,
            border: 'none',
            color: color.sidebarInkFaint,
          }}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            style={{ transform: pinned ? 'rotate(0deg)' : 'rotate(180deg)' }}
            aria-hidden="true"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
      </div>

      <nav
        aria-label="Workspace"
        style={{
          padding: expanded ? '14px 10px' : '14px 6px',
          display: 'flex',
          flexDirection: 'column',
          gap: expanded ? 2 : 6,
          flex: 1,
        }}
      >
        {expanded ? (
          <div
            style={{
              fontSize: '10px',
              fontWeight: 600,
              letterSpacing: '.9px',
              color: color.sidebarSectionLabel,
              padding: '8px 10px 6px',
            }}
          >
            WORKSPACE
          </div>
        ) : null}

        {entries.map((entry) => {
          // Two layouts, one item: a row when expanded, a centred icon-over-label column on the
          // rail. `isActive` washes the pill in the first case and the icon tile in the second.
          const inner = (isActive: boolean) =>
            expanded ? (
              <>
                {entry.icon}
                <span>{entry.label}</span>
                {entry.badge != null ? (
                  <span
                    className="mono"
                    style={
                      entry.badgeTone === 'alert'
                        ? {
                            marginLeft: 'auto',
                            fontSize: '10.5px',
                            fontWeight: 600,
                            color: color.onBadgeAlert,
                            background: color.badgeAlert,
                            borderRadius: radius.pill,
                            padding: '1px 7px',
                          }
                        : {
                            marginLeft: 'auto',
                            fontSize: '10px',
                            fontWeight: 600,
                            color: color.sidebarInkMuted,
                            background: color.sidebarHover,
                            borderRadius: radius.sm,
                            padding: '1px 6px',
                          }
                    }
                  >
                    {entry.badge}
                  </span>
                ) : null}
              </>
            ) : (
              <>
                <span
                  className="navtile"
                  style={{
                    width: 34,
                    height: 34,
                    flex: 'none',
                    borderRadius: radius['3xl'],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    // `undefined`, not `'transparent'`: an inline value would outrank the
                    // `.navitem-rail:hover .navtile` wash in global.css and kill the rail's hover.
                    background: isActive ? color.sidebarActive : undefined,
                    transition: 'background .12s ease',
                  }}
                >
                  {entry.icon}
                </span>
                <span style={{ fontSize: fontSize.label, lineHeight: 1.25 }}>{entry.label}</span>
              </>
            );

          const baseStyle = expanded
            ? ({
                display: 'flex',
                alignItems: 'center',
                gap: 11,
                padding: '9px 10px',
                borderRadius: radius.xl,
                fontSize: fontSize.navItem,
                fontWeight: 500,
                textDecoration: 'none',
              } as const)
            : ({
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                padding: '6px 2px 8px',
                borderRadius: radius['3xl'],
                fontWeight: 500,
                textAlign: 'center',
                textDecoration: 'none',
              } as const);

          if (!entry.enabled) {
            return (
              <span
                key={entry.label}
                className="nav-disabled"
                // Not focusable and not clickable — FR-023 requires "not navigable by click,
                // keyboard, or direct URL", and there is no route registered for it either.
                tabIndex={-1}
                aria-disabled="true"
                title={`${entry.label} — not available in this release`}
                style={{ ...baseStyle, color: color.sidebarInk }}
              >
                {inner(false)}
              </span>
            );
          }

          return (
            <NavLink
              key={entry.label}
              to={entry.to!}
              // The rail's hover belongs on the icon tile, not the whole column — see `.navitem`
              // and `.navitem-rail` in global.css.
              className={expanded ? 'navitem' : 'navitem navitem-rail'}
              // On the rail the count is not rendered, so the title is the only place it survives.
              title={!expanded && entry.badge != null ? `${entry.label} · ${entry.badge}` : undefined}
              style={({ isActive }) => ({
                ...baseStyle,
                background: isActive && expanded ? color.sidebarActive : 'transparent',
                color: isActive ? color.sidebarInkActive : color.sidebarInk,
              })}
            >
              {({ isActive }) => inner(isActive)}
            </NavLink>
          );
        })}
      </nav>

      <div
        style={{
          padding: '14px 16px',
          borderTop: `1px solid ${color.sidebarDivider}`,
          display: 'flex',
          alignItems: 'center',
          gap: 11,
          justifyContent: expanded ? 'flex-start' : 'center',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 32,
            height: 32,
            flex: 'none',
            borderRadius: radius.circle,
            background: color.sidebarAvatarBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 600,
            color: color.sidebarInkBody,
          }}
        >
          {initials}
        </div>

        {expanded ? (
          <div style={{ lineHeight: 1.25, minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: fontSize.bodyStrong,
                fontWeight: 600,
                color: color.sidebarInkStrong,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {me?.displayName ?? me?.email ?? 'Signed out'}
            </div>
            <div style={{ fontSize: fontSize.label, color: color.sidebarInkMuted }}>
              {me ? (me.role === 'admin' ? 'Administrator' : 'User') : '—'}
            </div>
          </div>
        ) : null}

        {expanded && onSignOut ? (
          <button
            type="button"
            onClick={onSignOut}
            style={{
              background: 'none',
              border: 'none',
              color: color.sidebarInkMuted,
              cursor: 'pointer',
              fontSize: fontSize.caption,
              padding: 4,
            }}
          >
            Sign out
          </button>
        ) : null}
        </div>
      </div>
    </aside>
  );
}
